/* eslint max-len: 'off' */

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, Link } from 'gatsby'
import { routePropTypes } from '../../utils/routes'
import HeaderFooterLayout from '../../layouts/headerfooter'
import GuideTile from './tile'

import './_guides.less'

const Links = [
    {
        route: 'platform-overview',
        title: 'Platform Overview',
        content: 'Read about the basics of mParticle, and learn how to find your way around the platform.',
        links: [
            { text: 'Introduction', link: 'introduction' },
            { text: 'Navigating mParticle', link: 'navigating-mparticle' },
            { text: 'The Overview Map', link: 'overview-map' },
        ],
    },
    {
        route: 'getting-started',
        title: 'Getting Started',
        content: 'Set up your first input to capture data before forwarding it to a downstream output.',
        links: [
            {
                text: 'Create Inputs',
                link: 'create-an-input',
            },
            {
                text: 'Start capturing data',
                link: 'start-capturing-data',
            },
            {
                text: 'Connect an Event Output',
                link: 'connect-an-event-output',
            },
            {
                text: 'Create an Audience',
                link: 'create-an-audience',
            },
        ],
    },
    {
        route: 'audiences',
        title: 'Audiences',
        content: 'Build audiences of related users using behavioral and event data.',
        links: [
            {
                text: 'Overview',
                link: 'overview',
            },
            {
                text: 'Create an Audience',
                link: 'create-an-audience',
            },
            {
                text: 'Predictive Audiences',
                link: 'predictive-audiences/overview',
            },
            {
                text: 'Composable Audiences',
                link: 'composable-audiences/overview',
            },
        ],
    },
    {
        route: 'customer-360',
        title: 'Customer 360',
        content:
            'Consolidate user data into profiles, configure how mParticle identifies users, and leverage user data to customize customer experiences.',
        links: [
            {
                text: 'User Profiles',
                link: 'profiles/overview',
            },
            {
                text: 'Identity Dashboard',
                link: 'identity-dashboard',
            },
            {
                text: 'IDSync',
                link: 'idsync/introduction',
            },
            {
                text: 'Identity Resolution Log',
                link: 'identity-resolution-log',
            },
            {
                text: 'Household Reach',
                link: 'household-reach',
            },
            {
                text: 'Calculated Attributes',
                link: 'calculated-attributes/overview',
            },
            {
                text: 'Predictions',
                link: 'predictions/overview',
            },
        ],
    },
    {
        route: 'data-platform',
        title: 'Data Platform',
        content:
            'Create data inputs and outputs, manage your data quality with data plans, and use rules and filters to manage what gets sent to your outputs.',
        links: [
            {
                text: 'Trends',
                link: 'trends',
            },
            {
                text: 'Connections',
                link: 'setup/connections',
            },
            {
                text: 'Plans',
                link: 'data-planning',
            },
            {
                text: 'Filters',
                link: 'data-filter',
            },
        ],
    },
    {
        route: 'data-warehouse',
        title: 'Data Warehouse',
        content:
            'Ingest data directly into mParticle with Warehouse Sync, or create composable audiences using user and event data stored in your warehouse.',
        links: [
            {
                text: 'Connections',
                link: 'composable-audiences/connections/overview',
            },
            {
                text: 'Data Models',
                link: 'composable-audiences/data-models/overview',
            },
            {
                text: 'Warehouse Sync',
                link: 'warehouse-sync/setup',
            },
        ],
    },
    {
        route: 'mparticle-agent',
        title: 'mParticle Agent',
        content:
            'Explore your data, prepare audiences, and build predictions through a conversational, agentic interface.',
        links: [
            {
                text: 'Overview',
                link: 'overview',
            },
            {
                text: 'Explore Your Data',
                link: 'explore-your-data',
            },
            {
                text: 'Build Audiences',
                link: 'build-audiences',
            },
            {
                text: 'Create Predictions',
                link: 'create-predictions',
            },
            {
                text: 'Data and Privacy',
                link: 'data-and-privacy',
            },
        ],
    },
    {
        route: 'oversight',
        title: 'Oversight',
        content:
            'Investigate data forwarding issues, trace data processing, and manage consent filters and data subject requests.',
        links: [
            {
                text: 'System Alerts',
                link: 'system-alerts',
            },
            {
                text: 'Observability',
                link: 'observability/overview',
            },
            {
                text: 'Data Subject Requests',
                link: 'privacy/data-subject-requests',
            },
            {
                text: 'Data Privacy Controls',
                link: 'privacy/data-privacy-controls',
            },
            {
                text: 'Consent Filters',
                link: 'privacy/consent-filters',
            },
        ],
    },
    {
        route: 'settings',
        title: 'Settings',
        content: 'Manage your mParticle account and settings.',
        links: [
            {
                text: 'Platform Configuration',
                link: 'platform/platform-configuration',
            },
            {
                text: 'mParticle Users and Roles',
                link: 'platform/users',
            },
            {
                text: 'Notifications',
                link: 'platform/notifications',
            },
            {
                text: 'Billing',
                link: 'billing/usage-and-billing-report',
            },
        ],
    },
]

const Guides = (props) => (
    <HeaderFooterLayout location={props.location} metadata={props.data.pageMetadata}>
        <div className='guides main-content'>
            <h1 id='guides-hub-title'>Guides</h1>
            <p className='large'>
                This section contains in-depth information about the mParticle platform and features.
                <ul>
                    <li>
                        For information about our SDKs, APIs, and tools, visit the{' '}
                        <Link to='/developers/'>Developers</Link> section.
                    </li>
                    <li>
                        For information about our latest product releases, visit the{' '}
                        <Link to='https://changelog.mparticle.com'>mParticle Changelog</Link>.{' '}
                    </li>
                </ul>
            </p>
            <div className='flex-content guide-tiles'>
                {Links.map((link) => (
                    <GuideTile key={`${link.title}${link.route}`} data={link} />
                ))}
            </div>
        </div>
    </HeaderFooterLayout>
)

export const frontmatter = {
    title: 'Guides',
    showWhenLast: true,
}

export const query = graphql`
    query {
        pageMetadata(path: { eq: "/guides/" }) {
            ...BreadcrumbMetadata
        }
    }
`

Guides.propTypes = {
    location: routePropTypes.location.isRequired,
    data: PropTypes.shape({
        pageMetadata: routePropTypes.pageMetadata,
    }).isRequired,
}

export default Guides
